import { apiClient } from '@core/api/ApiClientFactory';
import { Role, ActiveLicenses } from '@core/types';

export interface Organisation {
    id: string;
    name: string;
    activeLicenses?: ActiveLicenses[];
    users?: any[];
    deviceDisconnectNotificationRecipients?: string;
    recycleBinRetentionDays?: number;
    fastLanerOrgId?: string;
    ftpUserName?: string | null;
    ftpPassword?: string | null;
    ftpHost?: string | null;
    ftpPort?: number;
}

export interface UserApiResponse {
    id?: string;
    organisationId: string;
    organisationIds?: string[] | null;
    organisation?: Organisation | null;
    organisations?: Organisation[] | null;
    role?: Role;
    accessToAllStores?: boolean;
    shopIds?: string[] | null;
    groupId?: string | null;
    displayName?: string;
    surname?: string | null;
    givenName?: string | null;
    eMail: string;
    password?: string | null;
    accountEnabled?: boolean;
}

class UserService {
    private readonly baseUrl = 'user';

    /**
     * Get all users for the current organisation without super admins
     */
    async getAllWithoutSuperAdminsForCurentOrganisation(): Promise<UserApiResponse[]> {
        const response = await apiClient.get<UserApiResponse[]>(
            `${this.baseUrl}/getAllWithoutSuperAdminsForCurentOrganisation`
        );
        return response.data;
    }

    /**
     * Get all super admins
     */
    async getAllSuperAdmins(): Promise<UserApiResponse[]> {
        const response = await apiClient.get<UserApiResponse[]>(`${this.baseUrl}/superAmins`);
        return response.data;
    }

    /**
     * Get all users for all organisations
     */
    async getAllForAllOrganisations(): Promise<UserApiResponse[]> {
        const response = await apiClient.get<UserApiResponse[]>(`${this.baseUrl}/getAllForAllOrganisations`);
        return response.data;
    }

    /**
     * Get user by ID
     */
    async getById(id: string): Promise<UserApiResponse> {
        const response = await apiClient.get<UserApiResponse>(`${this.baseUrl}/${id}`);
        return response.data;
    }

    /**
     * Create a new user
     */
    async create(user: UserApiResponse): Promise<UserApiResponse> {
        const response = await apiClient.post<string>(`${this.baseUrl}`, user);
        return this.getById(response.data);
    }

    /**
     * Update an existing user
     */
    async update(id: string, user: UserApiResponse): Promise<void> {
        await apiClient.put<string>(`${this.baseUrl}/${id}`, user);
    }

    /**
     * Delete a user
     */
    async delete(id: string): Promise<void> {
        await apiClient.delete(`${this.baseUrl}/${id}`);
    }

    /**
     * Get user by email address
     */
    async getByMailAddress(email: string): Promise<UserApiResponse | null> {
        try {
            const users = await this.getAllWithoutSuperAdminsForCurentOrganisation();
            return users.find((u) => u.eMail === email) || null;
        } catch (error) {
            console.error('Failed to get user by email:', error);
            return null;
        }
    }

    async getUsers(): Promise<UserApiResponse[]> {
        try {
            const response = await apiClient.get<UserApiResponse[]>(
                `${this.baseUrl}/getAllWithoutSuperAdminsForCurentOrganisation`
            );
            return response.data;
        } catch (error) {
            console.error('Failed to load users:', error);
            return [];
        }
    }
}

export const userService = new UserService();
